/** Content safety (App Store Guideline 1.2): user reports and user blocks. See docs/release/CONTENT_SAFETY.md. */

export const REPORT_CONTENT_TYPES = ['comms', 'marker', 'broadcast', 'voice', 'user'] as const;
export type ReportContentType = (typeof REPORT_CONTENT_TYPES)[number];

export const REPORT_REASONS = ['harassment', 'hate', 'sexual', 'violence', 'spam', 'other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  harassment: 'Harassment or bullying',
  hate: 'Hate speech or slurs',
  sexual: 'Sexual content',
  violence: 'Threats or violence',
  spam: 'Spam',
  other: 'Something else',
};

/** The operator's documented response target for a new report. */
export const REPORT_RESPONSE_TARGET_HOURS = 24;

export type ReportStatus = 'open' | 'actioned' | 'dismissed';

export interface ReportRequest {
  missionId: string;
  contentType: ReportContentType;
  /** Required for comms / marker / broadcast; omitted for voice and user reports. */
  contentId?: string | null;
  /** Required for voice and user reports; resolved from the content otherwise. */
  reportedUserId?: string | null;
  reason: ReportReason;
  details?: string;
  /** Also block the reported user for the reporter. */
  alsoBlock?: boolean;
}

export interface ReportReceipt {
  id: string;
  status: ReportStatus;
  responseTargetHours: number;
  blocked: boolean;
}

export interface BlockedUser {
  userId: string;
  displayName: string;
  createdAt: number;
}

/** The public support address, used on every build unless the server sets APOS_SUPPORT_CONTACT. */
export const DEFAULT_SUPPORT_EMAIL = 'support@vaultlinesolutions.com';

export interface SupportContact {
  kind: 'email' | 'url';
  value: string;
}

export const DEFAULT_SUPPORT_CONTACT: SupportContact = { kind: 'email', value: DEFAULT_SUPPORT_EMAIL };

export interface SupportInfo {
  /** Support contact: APOS_SUPPORT_CONTACT when set, otherwise DEFAULT_SUPPORT_EMAIL. */
  contact: SupportContact;
  reportResponseHours: number;
}

/**
 * Service-wide account suspension (Guideline 1.2: act against abusive users). The reason is a
 * category only; the operator's note never leaves the server.
 */
export const SUSPENSION_REASONS = REPORT_REASONS;
export type SuspensionReason = ReportReason;
export const ACCOUNT_SUSPENDED_CODE = 'account_suspended';
/** WebSocket close code sent to a suspended user's open sockets. */
export const WS_CLOSE_ACCOUNT_SUSPENDED = 4423;

/** What a suspended user is told (in the 403 `account_suspended` error, as `error.suspension`). */
export interface AccountSuspensionNotice {
  reason: SuspensionReason;
  reasonLabel: string;
  since: number;
  /** Null for a suspension without an end date. */
  until: number | null;
  /** Where to appeal. */
  support: SupportContact;
}
